import { httpClient } from 'utils/http';
import {
	Vue, Component, Ref, toNative,
} from 'vue-facing-decorator';
import * as DB from 'interfaces/database';
import { BModal } from 'bootstrap-vue';
import {
	CellComponent, CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator,
} from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';
import mitt from 'mitt';
import Swal from 'sweetalert2';
import { TabEvent } from 'interfaces/app';
import CustomSelect from 'components/CustomSelect';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.settingsModel>>();
@Component({
	components: {
		TabulatorBtn,
		CustomSelect,
	},
	mixins: [Template],
})
class AppSettingTable extends Vue {
	@Ref('addappsetting-modal') private readonly modal!: BModal;

	@Ref('appSettingTable')
	private readonly tableReference!: HTMLDivElement;

	private isLoading = false;

	private visible = false;

	private index = 0;

	private settingsData = {} as Pick<DB.settingsModel, 'key' | 'value'>;

	private columnDefs: ColumnDefinitionExtended[] = [];

	private table?: Tabulator;

	private affiliateid: DB.AffiliateModel['id'] | null = null;

	private AffiliateData: DB.AffiliateModel[] = [];

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				field: 'key',
				title: 'Name',
			},
			{
				field: 'value',
				title: 'Value',
				editor: 'input',
				cellEdited: this.cellValueChanged,
			},
			{
				title: 'action',
				formatter: (cell: CellComponentExtended<DB.settingsModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'delete',
									eventName: 'deleteItem',
									className: 'fa-times',
								},
							],
							eventBus,
						},
					});
					return instance;
				},
			},
		] as ColumnDefinitionExtended[];
	}

	protected mounted(): void {
		eventBus.on(
			'deleteItem',
			this.deleteAppSettingItem,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeUnmount(): void {
		eventBus.off(
			'deleteItem',
			this.deleteAppSettingItem,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData();
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
			},
		);
	}

	private getData(): void {
		const parameter = new URLSearchParams({
			limit: '0',
		});
		const settingsParameter = new URLSearchParams({
			limit: '0',
		});
		if (this.affiliateid) {
			settingsParameter.append(
				'where',
				JSON.stringify({ affiliateid: this.affiliateid }),
			);
		}
		this.table?.alert('Loading');
		Promise.all([
			httpClient.get<DB.AffiliateModel[]>(`/api/affiliate?${parameter}`),
			httpClient.get<DB.settingsModel[]>(`/api/setting?${settingsParameter}`),
		])
			.then(([AffiliateResponse, settingResponse]) => {
				this.AffiliateData = AffiliateResponse.data;
				this.table?.setData(settingResponse.data);
				return undefined;
			}).finally(() => {
				this.table?.clearAlert();
			})
			.catch((err: any) => {
				this.$toastError(err.message);
			});
	}

	private cellValueChanged(cell: CellComponent): void {
		this.table?.alert('Loading');
		const rowData = cell.getData() as DB.settingsModel;
		httpClient.put<DB.settingsModel>(
			`/api/setting/${rowData.id}`,
			{
				value: cell.getValue(),
			},
		).then(() => {
			this.$toastSuccess(`${rowData.key} has been updated`);
			return undefined;
		}).finally(() => {
			this.table?.clearAlert();
		})
			.catch((error) => {
				this.$toastError(error.message);
			});
	}

	protected saveSetting(): void {
		this.table?.alert('Loading');
		httpClient.post<DB.settingsModel[]>(
			'/api/setting',
			{
				...this.settingsData,
				affiliateid: this.affiliateid,
			},
		).then((res) => {
			this.modal.hide();
			// add data to the ag-grid
			this.table?.addData(res.data);
			this.$toastSuccess(`${this.settingsData.key} has been added`);
			return undefined;
		}).finally(() => {
			this.table?.clearAlert();
		}).catch((error) => {
			this.$toastError(error.message);
		});
	}

	private deleteAppSettingItem(data: TabEvent<DB.settingsModel>['deleteItem']): void {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this file!',
			icon: 'warning',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-danger m-1',
				cancelButton: 'btn btn-secondary m-1',
			},
			confirmButtonText: 'Yes, delete it!',
			html: false,
		}).then((result) => {
			if (result.value) {
				this.table?.alert('Loading');
				// eslint-disable-next-line promise/no-nesting
				httpClient.delete(`/api/setting/${data.id}`).then(() => {
					this.$toastSuccess('Item Deleted');
					this.table?.deleteRow(data.id);
					return null;
				}).finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$toastError(err.message);
				});
			}
			return undefined;
		}).catch((err) => {
			this.$toastError(err.message);
		});
	}
}

export default toNative(AppSettingTable);
